import { CRUD, CrudModel } from '../../classes'
import { entriesToMap } from '../../utils'
import { Clinician } from '../clinician'
import { EligibilityBenefits } from '../eligibility'
import { InvoiceLineItem, InvoiceModel } from '../invoice'
import { ChargeGrouping } from './charge-grouping'
import { EstimateData } from './estimate.data'
import { EstimateCharge } from './estimate-charge'
import { EstimateInputs } from './estimate-inputs'
import { EstimateInsurances } from './estimate-insurances'
import { EstimateOutput } from './estimate-output'
import { HBPricerRequest, HBPricerResponse } from './hb-pricer-api'
import { ModelClaimSearchTrace } from './model-claim-search-trace'
import { PBPricerRequest, PBPricerResponse } from './pb-pricer-api'
import { PricerErrorResponse } from './pricer-error'
import { PricerRequestOptions } from './pricer-request'

@CRUD({
  base: '/estimates',
})
export class EstimateModel extends CrudModel {
  patientId: string
  facilityId: string
  templateId?: string
  appointmentIds: string[]
  status: Estimate.STATUS
  patientType?: Estimate.PATIENT_TYPE
  /** Immutable after creation when set. Nullable on legacy records. */
  mode?: Estimate.MODE | null
  estimateBalanceId?: string | null
  pricerRequestOptions?: PricerRequestOptions
  charges?: EstimateCharge[]
  pbRequests?: Map<ChargeGrouping, PBPricerRequest>
  hbRequests?: Map<ChargeGrouping, HBPricerRequest>
  pbResponses?: Map<ChargeGrouping, PBPricerResponse | PricerErrorResponse>
  hbResponses?: Map<ChargeGrouping, HBPricerResponse | PricerErrorResponse>
  inputs: EstimateInputs
  // Snapshot of eligibility's benefits as of the last time inputs.deductible/copayment/
  // coinsurance/outOfPocketMax were synced from it. Diffing inputs against this (not against
  // a live eligibility fetch) is how a manually-typed value is distinguished from one that
  // still matches eligibility - see estimate.component.ts's resolveBenefitField().
  originalBenefits?: EligibilityBenefits | null
  originalSecondaryBenefits?: EligibilityBenefits | null
  output?: EstimateOutput
  clinicianId?: string
  clinician?: Clinician
  chargeGroupings: ChargeGrouping[]
  insurances?: EstimateInsurances
  invoices?: InvoiceModel[]
  modelClaimSearchTraces?: ModelClaimSearchTrace[]

  constructor(data?: EstimateData) {
    super(data, true)

    if (data?.pbRequests) {
      this.pbRequests = entriesToMap(data.pbRequests)
    }
    if (data?.hbRequests) {
      this.hbRequests = entriesToMap(data.hbRequests)
    }
    if (data?.pbResponses) {
      this.pbResponses = entriesToMap(data.pbResponses)
    }
    if (data?.hbResponses) {
      this.hbResponses = entriesToMap(data.hbResponses)
    }
    if (data?.clinician) {
      this.clinician = new Clinician(data.clinician)
    }
    if (data?.invoices?.length) {
      this.invoices = data.invoices.map((inv) => new InvoiceModel(inv))
    }
    this.status = data?.status ?? Estimate.STATUS.DRAFT
    this.mode = data?.mode ?? null
    this.estimateBalanceId = data?.estimateBalanceId ?? null
    this.inputs = data?.inputs ?? {
      deductible: 0,
      copayment: 0,
      coinsurance: 0,
      outOfPocketMax: null,
      discount: 0,
      estimateType: 'patientpal',
      insuranceType: 'medical',
      secondaryDeductible: 0,
      secondaryCoinsurance: 0,
      secondaryCopayment: 0,
      secondaryOutOfPocketMax: null,
    }
    this.originalBenefits = data?.originalBenefits ?? null
    this.originalSecondaryBenefits = data?.originalSecondaryBenefits ?? null
    this.output = data?.output
    this.chargeGroupings = [
      { visitNumber: 1 },
    ]
  }

  get text(): string {
    return 'Estimate'
  }

  getChargesForGroup(chargeGrouping: ChargeGrouping): EstimateCharge[] {
    return this.charges?.filter(charge => {
      if (chargeGrouping.appointmentId != null && charge.appointmentIds?.includes(chargeGrouping.appointmentId)) {
        return true
      } else if (charge.visitNumbers?.includes(chargeGrouping.visitNumber)) {
        return true
      }
    })
  }

  getTotalPBCharges(chargeGrouping: ChargeGrouping): number {
    const charges = this.getChargesForGroup(chargeGrouping)
    // Get the total of all charges with a type of 'PB'. Remember to multiply the unit charge by the quantity
    return charges.reduce((total, charge) => {
      return charge.type === 'pb' ? total + (charge.unitCharge * charge.quantity) : total
    }, 0)
  }

  getTotalHBCharges(chargeGrouping: ChargeGrouping): number {
    const charges = this.getChargesForGroup(chargeGrouping)
    // Get the total of all charges with a type of 'HB'
    return charges.reduce((total, charge) => {
      return charge.type === 'hb' ? total + (charge.unitCharge * charge.quantity) : total
    }, 0)
  }

  get lineItems(): InvoiceLineItem[] {
    return this.charges?.map(charge => {
      const lineItem = {
        name: charge.type, // this isn't part of InvoiceLineItem, but it is apparently used in appointment estimate card
        description: charge.description ?? '',
        amount: charge.amount ?? (charge.unitCharge * charge.quantity),
        // expectedAmount: number // TODO
        currency: charge.currency,
        quantity: charge.quantity,
        unit_amount: charge.unitCharge,
      }

      return lineItem
    })
  }

  override toJSON() {
    return Object.assign(super.toJSON(), {
      patientId: this.patientId,
      facilityId: this.facilityId,
      templateId: this.templateId,
      appointmentIds: this.appointmentIds,
      status: this.status,
      patientType: this.patientType,
      mode: this.mode,
      estimateBalanceId: this.estimateBalanceId,
      charges: this.charges,
      inputs: this.inputs,
      originalBenefits: this.originalBenefits,
      originalSecondaryBenefits: this.originalSecondaryBenefits,
      insurances: this.insurances,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      invoices: this.invoices?.map((inv) => inv.toJSON?.() ?? inv),
    })
  }
}

export namespace Estimate {
  export enum MODE {
    SCHEDULED = 'scheduled',
    UNSCHEDULED = 'unscheduled',
  }

  export enum PATIENT_TYPE {
    InPatient = 'I',
    OutPatient = 'O',
  }

  export enum STATUS {
    /**
     * Calculate & Save clicked but not finalized
     */
    DRAFT = 'D',
    /**
     * Finalize button on estimate has been clicked, Invoice created
     */
    CONFIRMED = 'C',
    /**
     * Estimate is finalized, invoice message sent to patient, message succesfully delivered
     */
    DELIVERED = 'DL',
    /**
     * Estimate is finalized, invoice message sent to patient, message bounced
     */
    UNDELIVERED = 'UDL'
  }
}
